import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { MaterialModule } from '../../../../../../../modules/catch-core/third-party/material/material.module';
import { ColorPickerModule } from 'ngx-color-picker';

interface TargetField {
  id?: string;
  name?: string;
  fieldType: string;
  value?: {
    options?: any[];
  };
}

interface ColorDataItem {
  value: string;
  color: string;
}

interface TableColumnDataRow {
  bindingName: string;
  targetField: TargetField;
  shape: { name: string };
  defaultColor: string;
  colorData: ColorDataItem[];
}

@Component({
  selector: 'app-add-dialog',
  templateUrl: './add-dialog.component.html',
  styleUrls: ['./add-dialog.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, ColorPickerModule],
})
export class AddDialogComponent implements OnInit {
  // Inject dependencies using Angular 20 inject() function
  public dialogRef = inject(MatDialogRef<AddDialogComponent>);
  public data = inject(MAT_DIALOG_DATA);
  private dataService = inject(DataService);

  // Form controls
  public formControl = new FormControl('', [Validators.required]);

  // Form data
  public bindingName = '';
  public selectedShape = '';
  public defaultColor = '#296A88';
  public targetField: TargetField = {
    fieldType: 'default',
    value: { options: [] },
  };

  // Lists and configurations
  public colorData: ColorDataItem[] = [];
  public shapeList: any[] = [];
  public fieldList: TargetField[] = [];
  public columnConfigurations: any[] = [];

  // Color picker helper (if needed for template)
  public color = '#296A88';

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.columnConfigurations = this.dataService.getTableConfiguration();
    this.fieldList = this.constructFieldList();
    this.shapeList = this.columnConfigurations[2]?.['options'] || [];
  }

  public onNoClick(): void {
    this.dialogRef.close(0);
  }

  public onTargetFieldSelection(selectedField: TargetField): void {
    this.targetField = selectedField;
    this.colorData = [];

    if (this.targetField?.fieldType === 'Checkbox') {
      this.colorData = [
        { value: 'True', color: this.defaultColor },
        { value: 'False', color: this.defaultColor },
      ];
    } else if (this.targetField?.fieldType === 'DropdownSingle') {
      const dropdownOptions = this.targetField.value?.options;
      if (dropdownOptions && Array.isArray(dropdownOptions)) {
        this.colorData = dropdownOptions.map((option) => ({
          value: option,
          color: this.defaultColor,
        }));
      }
    }
  }

  public confirmAdd(): void {
    const newTableColumnDataRow: TableColumnDataRow = {
      bindingName: this.bindingName,
      targetField: this.targetField,
      shape: { name: this.selectedShape },
      defaultColor: this.defaultColor,
      colorData: [...this.colorData], // Create a copy to avoid reference issues
    };

    this.dataService.addRecord(newTableColumnDataRow);
    this.dialogRef.close(1);
  }

  /**
   * Constructs the field list by filtering out already used fields
   * @returns Array of available fields for selection
   */
  private constructFieldList(): TargetField[] {
    const currentRecordList = this.dataService.getAllCurrentData();

    // If no property bindings exist, return all available fields
    if (!currentRecordList || currentRecordList.length === 0) {
      return this.columnConfigurations[1]?.['options'] || [];
    }

    // Get all available fields and filter out already used ones
    const allFieldList = [...(this.columnConfigurations[1]?.['options'] || [])];
    const usedFieldIds = new Set(currentRecordList.map((record) => record.targetField?.id).filter((id) => id !== undefined));

    // Return fields that are not already in use
    return allFieldList.filter((field) => !usedFieldIds.has(field.id));
  }

  /**
   * Helper method to check if form is valid
   * @returns boolean indicating form validity
   */
  public isFormValid(): boolean {
    return !!(this.bindingName && this.targetField && this.targetField.fieldType !== 'default' && this.selectedShape);
  }

  /**
   * Updates the default color and applies it to all color data items
   * @param newColor The new default color
   */
  public onDefaultColorChange(newColor: string): void {
    this.defaultColor = newColor;
    // Optionally update all existing color data items
    this.colorData = this.colorData.map((item) => ({
      ...item,
      color: newColor,
    }));
  }

  /**
   * Updates a specific color in the color data array
   * @param index The index of the color to update
   * @param newColor The new color value
   */
  public onColorDataChange(index: number, newColor: string): void {
    if (this.colorData[index]) {
      this.colorData[index] = {
        ...this.colorData[index],
        color: newColor,
      };
    }
  }
}
